import type { Dayjs } from "dayjs"
import dayjs from "dayjs"
import type { DYMember } from "~/modules/nuxt3-module-doyac/composables/dy-members"
import type { DYPaymentRefundInfo } from "~/modules/nuxt3-module-doyac/composables/dy-payments"
import type { UpgradePackageData } from "~/modules/nuxt3-module-user-ingang/components/modal/IGUpgradePackageModal2.vue" // 2026-04-07 Modal2로 변경
import type { ApiListStructure, ApiSimpleResponseStructure, DYBankAccount, ImageSet } from "~~/modules/nuxt3-module-doyac/composables/common"
import {
  $dy__fetchWithToken,
  extractDataOrFailure,
  useDYFetchWithToken
} from "~~/modules/nuxt3-module-doyac/composables/fetch"
import type { IGCourse } from "./ig-course"
import { type IGLicenseContentsData } from "./ig-license-contents"
import { useDYAppConfig } from "~/modules/nuxt3-module-doyac/composables/config"
import type { EdgeCacheParams } from "~/modules/nuxt3-module-doyac/composables/edge-cache"

// ============================================ MODEL ============================================

interface LicenseContentData extends IGLicenseContentsData {
  nowLearningRate: number
  form: { passFail: boolean | null, judgementDate: string | null, isJudging: boolean, noticeDate: string | null, receiptNum: number }
}

export interface IGAbstractCoursePayment {
  no: number
  typedPk: string
  /**
   * 동시접속 기기 허용수
   */
  sameTimeViewCount: null | number
  s_day: string
  e_day: string
  money_yn: boolean
  money_day: string
  mMethod: string
  register_day: number
  isHugang: boolean
  abc_gubun: number
  p_id: string
  originalPrice: number
  price: number
  orderNo: string
  /**
   * **[DEPRECATED — `paymentKey` 를 쓸 것]** 거래번호(PG사).
   *
   * 백엔드가 `paymentKey` 와 **같은 값**을 넣지만 DB 칸이 짧아(varchar 30~128)
   * 긴 값이 **조용히 잘린다.** 구글 인앱 구매토큰은 144자라 어느 칸에도 온전히 안 들어간다.
   * 잘린 값은 토큰처럼 보이지만 토큰이 아니다 — 조회·대조에 쓰지 말 것.
   * (실측 2026-09-11: 투어 14,791건 / 맞춤결제 107건 / 자격증 31건이 잘려 있다.)
   */
  order_tid: string
  paymentKey?: string
  refund_yn: boolean
  related_no?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  userid?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  userName?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  mobile_pay?: boolean
  /**
   * 관리자 요청인 경우에만 존재함
   */
  lect_price?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  use_cash?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  coupon_discount?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  method?: {
    mMethod: number
    value: string
    text: string
  }
  /**
   * 관리자 요청인 경우에만 존재함
   */
  bank_name?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  inname?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  member?: Partial<DYMember>
  /**
   * 관리자 요청인 경우에만 존재함
   */
  inday?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  couponIssue?: any
  refund_date?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  refund_price?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  refund_star_cash?: number
  /**
   * 평생교육바우처(mMethod=8) 결제건의 등록 정보. `dy_lecture_book.voucher_info` JSON 컬럼.
   * 관리자 요청인 경우에만 존재함. 미등록 결제건은 null 이다.
   */
  voucher_info?: IGCoursePaymentVoucherInfo | null
}

/**
 * 평생교육바우처 등록 정보 JSON(`dy_lecture_book.voucher_info`).
 *
 * 기록 주체가 둘로 나뉜다.
 * - `isChanged` / `changedDate` — **강의(typedPk) 또는 수강기간(s_day/e_day)의 '월'이 변경**될 때
 *   `CoursePayment` 모델의 saving 이벤트가 기록 (2026-08-10 기준 확대·정밀화.
 *   같은 달 안에서 일자만 바뀐 건은 lllcard 가 월 단위로만 보관하므로 기록하지 않는다)
 * - 나머지 키 — 평생교육이용권 자동등록 봇(`python_apps/lifelong_voucher`)이 raw UPDATE 로 기록
 *
 * @see doyacingang/laravel/app/Model/doc_coursepayment_voucher_info.md
 */
export interface IGCoursePaymentVoucherInfo {
  /** 평생교육이용권 사이트(lllcard) 학습자 등록 완료 여부 */
  isRegist?: boolean
  /** 등록 시각 `YYYY-MM-DD HH:mm:ss`. (`registered` 오타지만 기존 컨벤션 유지) */
  registedDate?: string
  /** 사이트에 실제 노출된 강좌명. 봇이 한자/특수문자를 제거한 결과라 원본 강의제목과 다를 수 있다 */
  courseName?: string
  /** 승인번호 */
  approvalNo?: string
  /** 이수여부. 평생교육이용권에선 출결 = 이수 동일 개념 */
  cmplStatus?: '이수' | '미이수' | string
  /** 이수여부 확정 시각 `YYYY-MM-DD HH:mm:ss` */
  cmplDate?: string
  /** 강의 또는 수강기간(월)이 변경되어 lllcard 재동기화(삭제 후 재등록)가 필요함을 뜻하는 신호 */
  isChanged?: boolean
  /** 가장 최근 변경 시각 `YYYY-MM-DD HH:mm:ss` */
  changedDate?: string
  /** 등록 실패·보류 사유 */
  reasonFailed?: string
  /** 등록 실패·보류 기록 시각 `YYYY-MM-DD HH:mm:ss` */
  reasonFailedDate?: string
  /**
   * 사이트에 실제 등록된 강좌 시작월 `YYYY-MM`.
   * lllcard 의 강좌기간 입력이 `<input type="month">`(검증도 `fn_comm_validYm`)라
   * **연월까지만** 존재한다 — 일자 단위 값은 사이트에 애초에 없다.
   * 2026-08-10 이전 등록건은 값이 없을 수 있다(봇 백필 대상).
   */
  eduStartMon?: string
  /** 사이트에 실제 등록된 강좌 종료월 `YYYY-MM`. @see eduStartMon */
  eduEndMon?: string
  /**
   * lllcard 강좌 고유키(`fnViewEdu(eduCd)`).
   * 장바구니 일괄결제는 승인번호 하나가 강좌 여러 개에 걸리므로, 이 결제행이 어느
   * 강좌에 붙었는지는 승인번호가 아니라 이 값으로 확정한다. 화면 표시용은 아니다.
   * 2026-08-10 이전 등록건은 값이 없을 수 있다(봇 백필 대상).
   */
  eduCd?: string
  /**
   * **등록 시점의** 도약 강의 식별자(`dy_lecture_book.typedPk`) 스냅샷. 2026-08-10 추가.
   * 봇이 매 회차 현재 `typedPk` 와 대조해 다르면 = 등록 후 강의가 바뀐 것 →
   * lllcard 학습자 삭제 후 재등록.
   */
  typedPk?: string
  /**
   * **등록 시점의** 도약 수강 시작월 `YYYY-MM` 스냅샷. 2026-08-10 추가.
   * ⚠️ `eduStartMon`(사이트 강좌기간)과 혼동하지 말 것. 이 스냅샷은 **도약 변경 감지**용이라
   * 현재 `s_day` 와 비교한다.
   * 사이트 강좌기간은 별개 축으로 **결제월(`money_day`) ~ 수강종료월(`e_day`)** 과
   * 정확히 같은지 본다(2026-08-11 규칙 — 넓어도 불일치로 친다).
   */
  bookStartMon?: string
  /** **등록 시점의** 도약 수강 종료월 `YYYY-MM` 스냅샷. @see bookStartMon */
  bookEndMon?: string
  /**
   * 강제 재등록 요청 플래그. 스냅샷이 없는 옛 등록건 중 이미 어긋난 게 확인된 건에
   * 백필·수동으로 박아둔다. 봇이 재등록에 성공하면 지워진다.
   */
  needsRereg?: boolean
  /** 재등록이 필요한 사유(사람이 읽는 문자열) */
  needsReregReason?: string
}

export interface IGCoursePayment extends IGAbstractCoursePayment {
  addApiRegularCourse?: IGCourse
  /**
   * 해당 결제 모델이 포함하고 있는 자격증 데이터들
   */
  addApiRegularLicense?: LicenseContentData[]
  'flag': string,
  'type': string,
  'onflag': string,
  'melody_no': string,
  lecture?: string
  'status': string,
  'teacher': string,
  'teacherID': null | string,
  'total_price': string,
  'mobile': string,
  'related_book_no': null | number,
  'related_shop_no': null | number,
  'refund_msg': string,
  'refund_msg2': string,
  'simsLogAdPk': number,
  'package_parent_no': number,
  'period': number,
  'abcGubunKo': string,
  paidManualPrice?: number,
  'course': {
    'typedPk': string,
    'isPackage': false,
    'title': string,
    'teacher': string,
    'price': number,
    'hasAllNote': boolean,
    hasReferencePds: boolean
    'category': null | string[],
    'level': string,
    'mainImage': ImageSet,
    'akbo_no'?: number
    state?: string
    state2?: string
    state3?: string
    state4?: string
    state5?: string
    state6?: string
    /**
     * 곡별강의인지 여부
     */
    isMiniCourse?: boolean
    /**
     * 패키지인 경우 자식 typedPk 배열.
     */
    includedTypedPks?: string[],
    bundle_list_mode: 'bundleListGroup' | 'bundleListSelf'
    gubunLineShow: boolean
  },
  'courseInfo': {
    'lecture': string,
  },
  'subject': string
  fullLecture: string
  orderFlagText?: string
  isExtensionApplicable?: boolean
  isHugangApplicable?: boolean
  is_auto_refundable?: boolean
  /**
   * 관리자 요청인 경우에만 존재함
   */
  book_price?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  akbo_price?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  mr_price?: number
  /**
   * 관리자 요청인 경우에만 존재함
   */
  goods_price?: number
  hugang_check?: boolean
  /**
   * 관리자 요청인 경우에만 존재함
   */
  admin_extension?: boolean
  /**
   * 관리자 요청인 경우에만 존재함
   */
  recommenderID?: string
  /**
   * 관리자 요청인 경우에만 존재함
   */
  orderFlag?: number
  upgradePackageData?: UpgradePackageData[]
  currentChildrenCount?: number // 2026-04-07 현재 상품 구성품 수 (백엔드 응답)
  currentChildrenTypedPks?: string[] // 2026-04-07 현재 상품 구성품 typedPk 목록 (백엔드 응답)
}

export type IGCoursePaymentApiFields = Exclude<keyof IGCoursePayment, 'course' | 'courseInfo'>
  | { course: ('isPackage' | 'title' | 'level' | 'isMiniCourse' | '$all')[] }
  | { courseInfo: ('lecture')[] }
  | '$all'

/**
 * show요청에서 추가되는 값들
 */
export interface Show_IGCoursePayment extends IGCoursePayment {
  receipt: {
    lgdMid?: string
    authData?: string
    pgId?: string
  },
  cashReceipt: {
    orderNo?: string
    pgId?: string
    receiptKey?: string
    clientKey?: string
  }
  totalLectureNumber: number
  nowLearningNumber: number
  nowLearningRate: number
  openedLectureCount: number
  /**
   * 해당 결제건으로 수강한 강의 내역들만 계산. '정확히' 실제 본 수강시간을 계산한게 아님. 클릭해본 강의들의 시간들을 계산
   */
  totalLectureTime: string
}

export interface API_ActivePayment {
  no: number,
  flag: string,
  onflag: string,
  fullLecture: string,
  lecture: string,
  teacher: string,
  typedPk: string,
  mMethod: string | number,
  s_day: number,
  e_day: number,
  p_id: string,
}

/**
 * 환불 정보를 담는 인터페이스
 */
export interface IGCoursePaymentRefundInfo extends DYPaymentRefundInfo {
  isAkboMrIncluded: boolean
  isBookIncluded: boolean
  isGoodsIncluded: boolean
  courseUsageData: {
    includedCourse: {
      level: string
      lectureViewHistoryCount: number
      deductedPrice: number
      deductedPercent: number
      msg: string
    }[]
    totalLectureViewHistoryCount: number
    targetPrice: number
    sumOriginalPrice: number
    dateDiff: number
    deductedPeriod: number
    totalDeductedPrice: number
    refundCharge: number
    refundMoney: number
    refundStarCash: number
    refundPrice: number
    akboMrPrice: number
    mobilePrice: number
    abc_gubun: number
  }
  courseName: string
}

export interface ResponseOldCourseCheck {
  'old_type_1': {
    paymentChk: boolean
    /**
     * 이름이 타입드피케이지만 $course->no를 반환하는것으로 보아 number인듯
     */
    typedPk: number | null
  }
  'old_type_2': {
    paymentChk: boolean
    /**
     * 이름이 타입드피케이지만 $course->no를 반환하는것으로 보아 number인듯
     */
    typedPk: number | null
  }
  'old_type_3': {
    paymentChk: boolean
    /**
     * 이름이 타입드피케이지만 $course->no를 반환하는것으로 보아 number인듯
     */
    typedPk: number | null
  }
}

// ============================================ MODEL ============================================

// ============================================ REPOSITORY ============================================
export interface getActivePaymentParams {
  isTutoringOpen?: 'y' | 'n'
  excludeVoucher?: 'y' | 'n'
}

export interface CoursePaymentGetParams extends EdgeCacheParams {
  $fields?: IGCoursePaymentApiFields[]
  keyword?: string
  /**
   * 추가 모델 정보를 가져와야만 할때 ex)course
   */
  addApi?: string,
  /**
   * 해당 결제 모델이 포함하는 자격증 정보를 가져옴
   */
  addApiIncludedLicense?: string
  abc_gubun?: '1'|'2'|'3'|'4'
  page?: number,
  perPage?: number,
  userid?: string,
  /**
   * y, n
   */
  'money_yn'?: string,
  money_or_refund?: 'y'|'n'
  onflag?: string,
  /**
   * 현재 백엔드에서는 이 리퀘스트의 존재여부만 따지고 있으므로 아무거나 해도 되긴 함..
   */
  sugangIng?: string,
  /**
   * 피어리어드 사인값과 같이 사용해야 하며, 사인값을 안 보낼 경우 일치하는 것만 따지게 된다
   */
  period?: number,
  /**
   * 왼쪽일수록 컬럼값이 더 높고 오른쪽일수록 피어리어드 지정값이 더 높음
   */
  'period_sign'?: '>' | '>=' | '=' | '<' | '<=',
  /**
   * 연장 및 휴강 조건 타입. 일반적으론 잘 안쓸것 같음
   */
  extensionType?: 'y'
  requestCode?: 'isActivePayment' | 'isActivePaymentAll' | 'isFutureEdayPayment'
  groupBy?: string
  /**
   * 오더바이 쿼리에 그대로 쓸 수 있는 형태가 아닌, 특정 타입에 셋팅되는 오더바이를 불러오겠다는 개념. ex) license 라고 하면 자격증 타입에 맞는 오더바이 셋팅들을 그냥 적용함. no DESC 이렇게 보낼게 아님.
   */
  orderByType?: string
  /**
   * 특정 리퀘스트 범위를 더 가져오는 요청코드값.
   * full : 코스인포를 더해 가져온다
   */
  requestLevel?: 'full'
  /**
   * 전용코드
   */
  hugangService?: 'y'
  'dlb:flag'?: string
  'dlb:onflag'?: string
  mMethod?: string
  e_day?: string
  'register_day:start'?: number
  'register_day:end'?: number
  package_parent_no?: number
  /**
   * 관리자 유저 상세 페이지에서 노출될 결과를 위한 특수 파라미터. 이 값이 true이면 백엔드에서 money_yn == 'y' || refynd_yn == 'y'
   * 조건으로 검색된다.
   */
  forUserDetailAdminPage?: boolean
  /**
   * 패키지 업그레이드 페이지에서 노출될 결과를 위한 특수 파라미터. 이 값이 true이면 백엔드에서 패키지 업그레이드 결제 가능 모델만 조회된다.
   */
  availUpgradePackage?: 'y'
  /**
   * 업그레이드 결제 모델인지 여부
   */
  isUpgrade?: boolean
  sameTimeViewCount?: string
  /**
   * 2026-05-11 다운로드 기기 관리(초기화) 페이지 전용. 'y'를 보내면 백엔드에서 onflag='05'(묶음 껍데기 상품)을 결과에서 제외한다.
   */
  downloadDevice?: 'y'
}

interface RequestRefundParams extends DYBankAccount {
  reason?: string
}

/**
 *
 */
export interface IGAbstractCoursePaymentFormParams {
  typedPk?: string
  price?: number
  mMethod?: number
  lect_price?: number
  inname?: string
  bank_name?: string
  /**
   * 환불 처리일. null 을 보내면 비운다(2026-11-02 null 허용 추가)
   */
  refund_date?: string | null
  e_day?: string
  s_day?: string
  /**
   * 기간변경 요청의 유형.
   */
  periodChangeType?: 'extension' | 'hugang' | 'move'
  is_auto_refundable?: boolean
  related_no?: number
  sameTimeViewCount?: number
  money_yn?: boolean
  refund_yn?: boolean
  coupon_discount?: number
  third_party_fee?: number
  originalPrice?: number
  /**
   * 대표 상품 편집 모드. true로 설정하면 자식 객체들도 함께 편집된다.
   */
  __isRepresentativeMode?: boolean
}

export interface IGCoursePaymentFormParams extends IGAbstractCoursePaymentFormParams {
  orderFlag?: number
  book_price?: number
  akbo_price?: number
  mr_price?: number
  goods_price?: number
  recommenderID?: string
  period?: number
  hugang_check?: boolean
}

/**
 * 임시오픈 관련 정보를 담는 인터페이스
 */
export interface IGLectureExtraOpen {
  no: string
  lectureBookId: number
  originalStartDate: string
  originalEndDate: string
  startDate: string
  endDate: string
  insertedTime: string
}

export function useIGCoursePaymentManager() {
  return {

  }
}

export function useIGCoursePaymentApi() {
  const { API_BASE_URL } = useDYAppConfig()

  const getList = async (params?: CoursePaymentGetParams) => {
    const response = await getListPromise(params)

    return extractDataOrFailure(response)
  }

  const getListV2 = async (params?: CoursePaymentGetParams): Promise<ApiListStructure<IGCoursePayment>> => {
    const response = await useDYFetchWithToken<ApiListStructure<IGCoursePayment>>(
      `${API_BASE_URL}/api/course-payments/v2`,
      { query: { ...params, $fields: typeof params?.$fields === 'string' ? params?.$fields : JSON.stringify(params?.$fields) } }
    )
    return extractDataOrFailure(response)
  }

  const getListPromise = async(params?: CoursePaymentGetParams) => {
    const _params = {
      ...{
      },
      ...params
    }
    const response = await useDYFetchWithToken<ApiListStructure<IGCoursePayment>>(`${API_BASE_URL}/api/course-payments`, { query: _params, immediate: false })
    await response.execute({dedupe: 'defer'})
    return response
  }

  const get = async (pk: number, params?: CoursePaymentGetParams) => {
    const response = await getPromise(pk, params)
  
    return extractDataOrFailure(response)
  }

  const getPromise = async (pk: number, params?: CoursePaymentGetParams) => {
    const _params = {
      ...{
      },
      ...params
    }
    const response = await useDYFetchWithToken<IGCoursePayment>(`${API_BASE_URL}/api/course-payments/${pk}`, { query: _params, immediate: false })
    await response.execute({dedupe: 'defer'})
    return response
  }

  /**
   * 현재 유저가 수강중인 페이먼트들
   */
  const getActivePayment = async (params?: getActivePaymentParams) => {
    const _params = {
      ...{
        requestCode: 'isActivePayment'
      },
      ...params
    }
    const response = await useDYFetchWithToken<ApiListStructure<API_ActivePayment>>(`${API_BASE_URL}/api/course-payments`, { query: _params, immediate: false })
    await response.execute({dedupe: 'defer'})
    return extractDataOrFailure(response)
  }

  const getRefundInfo = async (paymentNo: number, date?: Dayjs | Date, isTour?: boolean) => {
    const _date = (date instanceof Date) ? dayjs(date) : date
    const apiUrl = isTour ? `${API_BASE_URL}/api/tour-payments/refund/info` : `${API_BASE_URL}/api/course-payments/refund/info`

    const response = await useDYFetchWithToken<IGCoursePaymentRefundInfo>(
      `${apiUrl}/${paymentNo}`,
      { query: { date: _date?.format('YYYY-MM-DD') }, immediate: false }
    )

    await response.execute({dedupe: 'defer'})
    return extractDataOrFailure(response)
  }

  const requestRefund = async (paymentNo: number, params?: RequestRefundParams, isTour?: boolean) => {
    const apiUrl = isTour ? `${API_BASE_URL}/api/tour-payments/refund/request` : `${API_BASE_URL}/api/course-payments/refund/request`
    return await $dy__fetchWithToken<ApiSimpleResponseStructure>(`${apiUrl}/${paymentNo}`, { method: 'post', body: params })
  }

  /**
   * 해당 typedPk에 해당하는 강의에 대한 권한체크
   * @param typedPk
   * @param params
   */
  const hasAuth = async (typedPk: string, params?: { adminFreePass: string, payNo: number }) => {
    const response = await hasAuthPromise(typedPk, params)
    return extractDataOrFailure(response)
  }

  const hasAuthPromise = async (typedPk: string, params?: { adminFreePass: string, payNo: number }) => {
    const response = await useDYFetchWithToken<{ success: boolean, message: string, stateLevel: number, paymentNo?: number }>(`${API_BASE_URL}/api/course-payments/has-auth/${typedPk}`, { query: params, immediate: false })
    await response.execute({dedupe: 'defer'})
    return response
  }

  /**
   * 원본 강의의 typedPk가 옴
   * @param coursePaymentNo
   */
  const hasAuthRe = async (coursePaymentNo: number) => {
    const response = await useDYFetchWithToken<string>(`${API_BASE_URL}/api/course-payments/has-auth-re/${coursePaymentNo}`, { immediate: false })
    await response.execute({dedupe: 'defer'})
    return extractDataOrFailure(response)
  }

  /**
   * 원본 강의의 typedPk가 옴
   * @param coursePaymentNo
   */
  const hasAuthExtension = async (coursePaymentNo: number) => {
    const response = await useDYFetchWithToken<string>(`${API_BASE_URL}/api/course-payments/has-auth-extension/${coursePaymentNo}`, { immediate: false })
    await response.execute({dedupe: 'defer'})
    return extractDataOrFailure(response)
  }

  /**
   * 휴강 권한 체크
   * @param pk
   */
  const hugangChk = async (pk: number) => {
    const response = await useDYFetchWithToken<ApiSimpleResponseStructure>(`${API_BASE_URL}/api/course-payments/hugang-chk/${pk}`, { immediate: false })
    await response.execute({dedupe: 'defer'})
    return extractDataOrFailure(response)
  }

  /**
   * 휴강 시작
   * @param pk
   * @param hugangDays
   */
  const hugangStart = async (pk: number, hugangDays: number) => {
    return await $dy__fetchWithToken<ApiSimpleResponseStructure>(`${API_BASE_URL}/api/course-payments/hugang-start/${pk}`, { method: 'patch', body: { hugangDays } })
  }

  /**
   * 휴강 취소
   * @param pk
   */
  const hugangEnd = async (pk: number) => {
    return await $dy__fetchWithToken<ApiSimpleResponseStructure>(`${API_BASE_URL}/api/course-payments/hugang-end/${pk}`, { method: 'patch' })
  }

  const oldCourseChk = async (typedPk: string, params?: {}) => {
    const response = await useDYFetchWithToken<null | ResponseOldCourseCheck>(`${API_BASE_URL}/api/course-payments/has-auth/${typedPk}`, { query: params, immediate: false })
    await response.execute({dedupe: 'defer'})
    return extractDataOrFailure(response)
  }

  /**
   * 결제 처리 메서드. 관리자 전용!
   */
  const approve = async (pk: number) => {
    return await $dy__fetchWithToken<IGCoursePayment>(`${API_BASE_URL}/api/course-payments/${pk}/approve`, { method: 'post', body: { no: pk } })
  }

  /**
   * 10일 연장 on/off 메서드. 관리자 전용!
   */
  const toggle10dExtension = async (pk: number) => {
    return await $dy__fetchWithToken<IGCoursePayment>(`${API_BASE_URL}/api/course-payments/${pk}/toggle-10d-extension`, { method: 'post', body: { no: pk } })
  }

  /**
   * 관리자 전용!
   */
  const patch = async (pk: number, params: IGCoursePaymentFormParams): Promise<IGCoursePayment> => {
    return await $dy__fetchWithToken<IGCoursePayment>(`${API_BASE_URL}/api/course-payments/${pk}`, { method: 'patch', body: params })
  }

  /**
   * 관리자 전용!
   */
  const remove = async (pk: number) => {
    return await $dy__fetchWithToken<IGCoursePayment>(`${API_BASE_URL}/api/course-payments/${pk}`, { method: 'delete' })
  }

  /**
   * 관리자 전용! 임시오픈 기능
   */
  const storeExtraOpen = async (pk: number, days: number) => {
    return await $dy__fetchWithToken<IGCoursePayment>(
      `${API_BASE_URL}/api/course-payments/${pk}/extra-opens`,
      { method: 'post', body: { days } }
    )
  }

  /**
   * 관리자 전용! 임시오픈 목록 조회 API
   */
  const listExtraOpens = async (pk: number) => {
    const response = await useDYFetchWithToken<IGLectureExtraOpen[]>(
      `${API_BASE_URL}/api/course-payments/${pk}/extra-opens`,
      { immediate: false }
    )
    await response.execute({dedupe: 'defer'})

    return extractDataOrFailure(response)
  }

  /**
   * [2026-05-26] 엑셀 다운로드 공통 처리.
   * DYfetch 응답이 Blob 이 아닐 때(서버 에러·빈 응답 등) `URL.createObjectURL(null)` 이 호출되어
   * "Overload resolution failed" 로 SPA 전체가 크래시되던 문제를 막기 위한 가드.
   * 실패 시 호출부에서 안내할 수 있도록 서버 메시지를 담은 Error 를 던진다.
   */
  const triggerExcelDownload = (
    response: { data: { value: unknown }, error: { value: unknown } },
    fileName: string,
  ) => {
    const blob = response.data.value
    if (response.error.value || !(blob instanceof Blob)) {
      const err = response.error.value as any
      const message = err?.data?.message || err?.message || '엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.'
      throw new Error(message)
    }

    // DYfetch는 헤더 분석이 안되므로, 가상의 앵커 태그를 만들어서 다운 후 삭제하는 방식 사용
    const a = window.document.createElement('a')
    const objectUrl = window.URL.createObjectURL(blob)
    a.href = objectUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(objectUrl)
  }

  /**
   * [2026-04-28] 평생교육이용권 결제내역 엑셀 다운로드 (관리자 전용)
   * 기존 PHP세션 기반 /apple_ingang/.../excelDownloadByLifeLongEducation.php 를 토큰 기반으로 전환
   */
  const lifelongEducationPaymentExcel = async (params: { startDate: string, endDate: string }) => {
    const response = await useDYFetchWithToken(
      `${API_BASE_URL}/api/course-payments/lifelong-education/payment-excel`,
      { method: 'get', query: params, responseType: 'blob', immediate: false }
    )
    await response.execute({ dedupe: 'defer' })

    triggerExcelDownload(response, `${params.startDate}_${params.endDate}_평생교육이용권_결제내역.xlsx`)
  }

  /**
   * 관리자 전용! 평생교육이용권 출결내역 엑셀 다운로드
   * [2026-04-28] 평생교육이용권 출결내역 엑셀 다운로드 (관리자 전용)
   * 기존 PHP세션 기반 /apple_ingang/.../excelDownloadByLifeLongEducationAttendance.php 를 토큰 기반으로 전환
   */
  const lifelongEducationAttendanceExcel = async (params: { startDate: string, endDate: string }) => {
    const response = await useDYFetchWithToken(
      `${API_BASE_URL}/api/course-payments/lifelong-education/attendance-excel`,
      { method: 'get', query: params, responseType: 'blob', immediate: false }
    )
    await response.execute({ dedupe: 'defer' })

    triggerExcelDownload(response, `${params.startDate}_${params.endDate}_평생교육이용권_출결내역.xlsx`)
  }

  return {
    getList, getListV2, getListPromise, get, getPromise, getActivePayment, getRefundInfo, requestRefund,
    hasAuth, hasAuthPromise, hasAuthRe, hasAuthExtension, hugangChk, hugangStart, hugangEnd, oldCourseChk,
    approve, toggle10dExtension, patch, remove, storeExtraOpen, listExtraOpens,
    lifelongEducationPaymentExcel, lifelongEducationAttendanceExcel
  }
}
